<!-- Content Wrapper Start -->
<div class="fm-content-wrapper">
  <div class="container-fluid">

    <!-- ═══════════════════════════════════════════════════════════════════
         State 1 — API / network error
         ═══════════════════════════════════════════════════════════════════ -->
    @if (hasError()) {
      <div class="fm-white-column text-center py-5">
        <h4 class="fm-head-24 fm-head-sty2">Something went wrong</h4>
        <p class="mb-3">We couldn't load this story. Please check your connection and try again.</p>
        <button type="button" class="fm-common-tbtn" (click)="loadStory()">
          Try Again <img class="fm-svg" src="images/icons/fm-arrow-ar.svg" alt="Retry" />
        </button>
      </div>
    }

    <!-- ═══════════════════════════════════════════════════════════════════
         State 2 — Story loaded successfully
         ═══════════════════════════════════════════════════════════════════ -->
    @else if (story()) {

      <!-- Back navigation -->
      <button type="button" class="fm-back-btn mb-4" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>Back to Stories
      </button>

      <!-- Stories Details Content Start -->
      <div class="row g-4">

        <!-- ═══════════════════════════════════════════════════════════════
             LEFT COLUMN — media player + transcript
             ═══════════════════════════════════════════════════════════════ -->
        <div class="col-md-7 col-xl-8 col-xxl">

          <!-- ── Card 1: Story media + metadata ──────────────────────── -->
          <div class="fm-white-column">
            <div class="fm-stories-blog fm-stories-details">

              <!-- Video / media thumbnail (player TBD) -->
              <div class="fm-story-vplayer">
                <img
                  class="fm-story-image img-fluid"
                  [src]="story()!.thumbnailUrl || 'images/stories/stories-post-1.png'"
                  [alt]="story()!.title"
                  onerror="this.src='images/stories/stories-post-1.png'"
                />
                <button type="button" class="fm-stories-pp fm-pp-large" aria-label="Play story">
                  <img src="images/icons/fm-play.svg" alt="Play Icon" />
                </button>
              </div><!-- /fm-story-vplayer -->

              <!-- Story metadata row -->
              <div class="fm-stories-content">
                <div class="row">

                  <!-- Left: category, duration/date, title, author -->
                  <div class="col-xl-6">
                    <div class="fm-st-promt-tag">
                      <span class="fm-st-ti">{{ story()!.categoryName }}</span>
                      <!-- Duration formatted + relative date of upload -->
                      <p class="fw-light mb-0">{{ formatDuration(story()!.duration) }}, {{ getRelativeTime(story()!.createdOn) }}</p>
                    </div>
                    <h4 class="fm-head-20 fm-head-sty2 mb-0">{{ story()!.title }}</h4>
                    <p class="fw-light mb-0">by {{ story()!.uploadedBy }}</p>

                    <!-- Engagement details: View count + Reaction count -->
                    <div class="fm-engagement-row mt-2">
                      <span class="fm-engagement-item">
                        <img class="fm-svg" src="images/icons/fm-eye.svg" alt="Views" />
                        {{ story()!.viewCount }} <span class="fm-engagement-label">Views</span>
                      </span>
                      <span class="fm-engagement-item">
                        <img class="fm-svg" src="images/icons/fm-heart-cl.svg" alt="Reactions" />
                        {{ story()!.reactionCount }} <span class="fm-engagement-label">Reactions</span>
                      </span>
                    </div>
                  </div>

                  <!-- Right: reaction buttons -->
                  <div class="col-xl-6">
                    <p class="fm-black-cl fm-fw-medium">Reaction</p>

                    <!-- Primary reaction buttons: Heart + Love -->
                    <div class="fm-react-btns">

                      <!-- ❤ Heart reaction (ReactionType.Heart = 0) -->
                      <button
                        type="button"
                        class="fm-react-btn"
                        [class.fm-react-btn-active]="currentUserReaction() === 0"
                        (click)="toggleReaction(0)"
                        [disabled]="isReacting()"
                        aria-label="Heart reaction"
                      >
                        <img src="images/icons/fm-heart-cl.svg" alt="Like" />
                        {{ formatReactionCount(0) }}
                      </button>

                      <!-- 😊 Love / Smile reaction (ReactionType.Smile = 1) -->
                      <button
                        type="button"
                        class="fm-react-btn"
                        [class.fm-react-btn-active]="currentUserReaction() === 1"
                        (click)="toggleReaction(1)"
                        [disabled]="isReacting()"
                        aria-label="Love reaction"
                      >
                        <img src="images/icons/fm-love.svg" alt="Love" />
                        {{ formatReactionCount(1) }}
                      </button>

                      <!-- Add Reaction — toggles secondary picker (Wow / Sad) -->
                      <button
                        type="button"
                        class="fm-react-btn"
                        [class.fm-react-btn-active]="isReactionPickerOpen()"
                        (click)="toggleReactionPicker()"
                        [disabled]="isReacting()"
                        aria-label="Add a reaction"
                      >
                        <img class="fm-svg" src="images/icons/fm-plus-gry.svg" alt="Add" />
                        Add Reaction
                      </button>

                    </div><!-- /fm-react-btns -->

                    <!-- Secondary picker: Wow + Sad — visible when "Add Reaction" is clicked -->
                    @if (isReactionPickerOpen()) {
                      <div class="fm-react-btns mt-2">

                        <!-- 😮 Wow reaction (ReactionType.Wow = 2) -->
                        <button
                          type="button"
                          class="fm-react-btn"
                          [class.fm-react-btn-active]="currentUserReaction() === 2"
                          (click)="toggleReaction(2)"
                          [disabled]="isReacting()"
                          aria-label="Wow reaction"
                        >
                          😮 {{ formatReactionCount(2) }}
                        </button>

                        <!-- 😢 Sad reaction (ReactionType.Sad = 3) -->
                        <button
                          type="button"
                          class="fm-react-btn"
                          [class.fm-react-btn-active]="currentUserReaction() === 3"
                          (click)="toggleReaction(3)"
                          [disabled]="isReacting()"
                          aria-label="Sad reaction"
                        >
                          😢 {{ formatReactionCount(3) }}
                        </button>

                        <!-- In-flight spinner while API call is pending -->
                        @if (isReacting()) {
                          <span
                            class="spinner-border spinner-border-sm align-self-center ms-1"
                            role="status"
                            aria-hidden="true"
                          ></span>
                        }

                      </div>
                    }

                    <!-- ── Reaction Attribution ─────────────────────────────
                         Shows who reacted, grouped by emoji type.
                         Current user's reaction is displayed as "You" (highlighted).
                         Other members are shown by their display name.
                         ────────────────────────────────────────────────── -->
                    @if (reactionGroups().length > 0) {
                      <div class="fm-reaction-attribution mt-3">
                        @for (group of reactionGroups(); track group.type) {
                          <div class="fm-reaction-attr-row">
                            <!-- Emoji badge for this reaction type -->
                            <span class="fm-reaction-attr-emoji">{{ group.emoji }}</span>
                            <!-- Comma-separated name chips; "You" is styled differently -->
                            <div class="fm-reaction-attr-names">
                              @for (name of group.names; track $index) {
                                <span
                                  class="fm-reactor-chip"
                                  [class.fm-reactor-you]="name === 'You'"
                                >{{ name }}</span>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    }

                  </div><!-- /col-xl-6 (reactions) -->
                </div><!-- /row -->
              </div><!-- /fm-stories-content -->

            </div><!-- /fm-stories-blog -->
          </div><!-- /fm-white-column card 1 -->

          <!-- ── Card 2: Full Transcript accordion ───────────────────── -->
          <div class="fm-white-column mt-4">
            <div class="accordion fm-accordion fm-accordion-st2" id="fmTranscriptAccordion">
              <div class="accordion-item fm-accordion-item mt-0">

                <h2 class="accordion-header fm-accordion-header">
                  <!-- Controlled by Angular signal — no Bootstrap JS needed -->
                  <button
                    class="accordion-button fm-accordion-button"
                    [class.collapsed]="!transcriptOpen()"
                    type="button"
                    (click)="toggleTranscript()"
                    [attr.aria-expanded]="transcriptOpen()"
                    aria-controls="fmFullTranscriptCollapse"
                  >
                    Full Transcript
                  </button>
                </h2>

                <!-- collapse / collapse.show controlled by Angular binding -->
                <div
                  id="fmFullTranscriptCollapse"
                  class="accordion-collapse fm-accordion-collapse collapse"
                  [class.show]="transcriptOpen()"
                >
                  <div class="accordion-body fm-accordion-body">
                    <div class="fm-border-column">

                      @if (getTranscriptText()) {
                        <!-- Transcript is read-only — viewer cannot edit -->
                        <p class="fm-dark fm-transcript-text">{{ getTranscriptText() }}</p>
                      } @else {
                        <!-- No transcript was generated for this story -->
                        <p class="text-muted fst-italic mb-0">No transcript available for this story.</p>
                      }

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div><!-- /fm-white-column card 2 -->

        </div><!-- /col-md-7 (left column) -->

        <!-- ═══════════════════════════════════════════════════════════════
             RIGHT SIDEBAR — actions / story details / related stories
             ═══════════════════════════════════════════════════════════════ -->
        <div class="col-md-5 col-xl-4 col-xxl-auto">

          <!-- ── Sidebar 1: Action buttons (Share / Download / Report) ── -->
          <div class="fm-sidebar-clumn">
            <div class="fm-border-column fm-color-brd">
              <div class="row g-3">
                <div class="col-12">
                  <!-- Share Story — modal implementation TBD -->
                  <button type="button" class="fm-common-btn fm-large-btn w-100 mt-0">
                    <img class="fm-svg" src="images/icons/fm-share.svg" alt="Share Icon" /> Share Story
                  </button>
                </div>
                <div class="col-12">
                  <!-- Download — file download TBD -->
                  <button type="button" class="fm-common-btn fm-secondary-btn fm-large-btn w-100 mt-1">
                    <img class="fm-svg" src="images/icons/fm-download.svg" alt="Download Icon" /> Download
                  </button>
                </div>
                <div class="col-12">
                  <!-- Report Story — report modal TBD -->
                  <button type="button" class="fm-common-btn fm-border-btn fm-large-btn w-100 mt-1">
                    <img class="fm-svg" src="images/icons/fm-flag.svg" alt="Flag Icon" /> Report Story
                  </button>
                </div>
              </div>
            </div>
          </div><!-- /fm-sidebar-clumn actions -->

          <!-- ── Sidebar 2: Story Details ────────────────────────────── -->
          <div class="fm-sidebar-clumn">
            <div class="fm-border-column fm-color-brd">
              <div class="row">
                <div class="col-12">
                  <h3 class="fm-head-20 fm-sidebar-title">Story Details</h3>
                </div>
                <div class="col-12">
                  <div class="fm-story-details-grid">

                    <!-- Date — hidden when not provided by API -->
                    @if (story()!.storyDate) {
                      <div class="fm-detail-item">
                        <div class="fm-detail-icon">
                          <img class="fm-svg" src="images/icons/fm-calendar.svg" alt="Date Icon" />
                        </div>
                        <div class="fm-detail-content">
                          <h4 class="fm-detail-heading">Date</h4>
                          <p class="fm-detail-text">{{ formatStoryDate(story()!.storyDate) }}</p>
                        </div>
                      </div>
                    }

                    <!-- Location — hidden when not provided -->
                    @if (story()!.location) {
                      <div class="fm-detail-item">
                        <div class="fm-detail-icon">
                          <img class="fm-svg" src="images/icons/fm-location.svg" alt="Location Icon" />
                        </div>
                        <div class="fm-detail-content">
                          <h4 class="fm-detail-heading">Location</h4>
                          <p class="fm-detail-text">{{ story()!.location }}</p>
                        </div>
                      </div>
                    }

                    <!-- People (tagged) — always visible; shows — when no one is tagged -->
                    <div class="fm-detail-item">
                      <div class="fm-detail-icon">
                        <img class="fm-svg" src="images/icons/fm-people-grp.svg" alt="People Icon" />
                      </div>
                      <div class="fm-detail-content">
                        <h4 class="fm-detail-heading">People</h4>
                        <p class="fm-detail-text">
                          {{ story()!.taggedPeople.length > 0 ? getTaggedPeopleNames() : '—' }}
                        </p>
                      </div>
                    </div>

                    <!-- Emotional Tone — hidden when not provided -->
                    @if (story()!.emotionalTone) {
                      <div class="fm-detail-item">
                        <div class="fm-detail-icon">
                          <img class="fm-svg" src="images/icons/fm-heart-pr.svg" alt="Emotional Tone Icon" />
                        </div>
                        <div class="fm-detail-content">
                          <h4 class="fm-detail-heading">Emotional Tone</h4>
                          <p class="fm-detail-text">{{ story()!.emotionalTone }}</p>
                        </div>
                      </div>
                    }

                  </div><!-- /fm-story-details-grid -->
                </div>
              </div>
            </div>
          </div><!-- /fm-sidebar-clumn story details -->

          <!-- ── Sidebar 3: Related Stories ──────────────────────────────
               Shows up to 2 most recent stories in the same category.
               Hidden entirely when list is empty.
               Each card is fully clickable and navigates to that story.
               ─────────────────────────────────────────────────────── -->
          @if (story()!.relatedStories.length > 0) {
            <div class="fm-sidebar-clumn">
              <div class="fm-border-column fm-color-brd">
                <div class="row">
                  <div class="col-12">
                    <h3 class="fm-head-20 fm-sidebar-title">Related Stories</h3>
                  </div>
                  <div class="col-12">
                    <div class="fm-related-stories-list">

                      @for (related of story()!.relatedStories; track related.storyId) {
                        <!-- Clickable card — routerLink navigates to story detail -->
                        <a
                          [routerLink]="['/app/stories', related.storyId]"
                          class="fm-related-story-card"
                          [attr.aria-label]="'View story: ' + related.title"
                        >
                          <!-- Thumbnail with duration badge overlay -->
                          <div class="fm-related-thumb">
                            <img
                              class="fm-related-cover"
                              [src]="related.thumbnailUrl || 'images/stories/stories-post-1.png'"
                              [alt]="related.title"
                              onerror="this.src='images/stories/stories-post-1.png'"
                            />
                            <span class="fm-tag fm-related-duration-tag">
                              {{ formatDuration(related.duration) }}
                            </span>
                          </div>

                          <!-- Story info block -->
                          <div class="fm-related-info">
                            <h4 class="fm-related-story-title">{{ related.title }}</h4>
                            <div class="fm-related-meta-row">
                              <!-- Category name shared with parent story -->
                              <span class="fm-related-story-meta">{{ story()!.categoryName }}</span>
                              <!-- View count -->
                              <span class="fm-related-stat">
                                <img class="fm-svg" src="images/icons/fm-eye.svg" alt="Views" />
                                {{ related.viewCount }}
                              </span>
                            </div>
                            <!-- Relative upload date -->
                            <p class="fm-related-date">{{ getRelativeTime(related.createdOn) }}</p>
                          </div>
                        </a>
                      }

                    </div>
                  </div>
                </div>
              </div>
            </div>
          }<!-- /related stories -->

        </div><!-- /col-md-5 (right sidebar) -->

      </div><!-- /row (stories details) -->
      <!-- Stories Details Content End -->

    }<!-- /@else if story() -->

  </div><!-- /container-fluid -->
</div><!-- /fm-content-wrapper -->
<!-- Content Wrapper End -->

